// Helper pra exibir erros amigáveis ao usuário final.
//
// REGRA (memory feedback-user-friendly-errors): NUNCA exibir códigos crus,
// DOMException raw, ou mensagens em inglês vindas de fetch/lib. Sempre passar
// pelo `friendlyError()` antes de mostrar pro usuário.
//
// Uso:
//   try { ... } catch (err) {
//     setError(friendlyError(err));
//   }
//
// Para erros de domínio específicos, o caller pode passar um `overrides` por
// código:
//   friendlyError(err, {
//     "user_not_found": "Email não cadastrado.",
//     "duplicate_email": "Já existe conta com esse email."
//   })

export interface FriendlyOptions {
  /** Overrides por código (string vinda do backend ou err.code). */
  overrides?: Record<string, string>;
  /** Mensagem default caso nenhum match. */
  fallback?: string;
}

const DEFAULT_FALLBACK =
  "Não foi possível concluir agora. Verifique sua conexão e tente de novo.";

// Mapeamentos de códigos comuns retornados pelo backend → mensagens PT-BR.
// Lista cresce conforme detectamos códigos novos no campo.
const CODE_TO_PT: Record<string, string> = {
  // Auth
  unauthenticated: "Sua sessão expirou. Faça login de novo.",
  unauthorized: "Você não tem permissão pra fazer isso.",
  forbidden: "Acesso negado.",
  not_allowed: "Operação não permitida.",
  invalid_credentials: "Email ou senha incorretos.",
  account_suspended: "Sua conta está suspensa. Contate o administrador.",
  must_change_password: "Você precisa trocar sua senha antes de continuar.",
  // Validação
  bad_request: "Algo na requisição está inválido.",
  invalid_email: "Email inválido.",
  weak_password: "Senha não atende aos requisitos mínimos.",
  password_mismatch: "As senhas não conferem.",
  invalid_input: "Algum campo está inválido.",
  too_large: "Conteúdo grande demais.",
  bad_phone: "Telefone inválido.",
  bad_initials: "Iniciais aceitam no máximo 4 caracteres.",
  // Estado / conflitos
  not_found: "Não encontrado.",
  already_exists: "Esse item já existe.",
  already_registered: "Esse email já está cadastrado.",
  bad_state: "A operação não pode ser feita no estado atual.",
  // Pagamento
  payment_failed: "O pagamento não foi aprovado. Tente outro método.",
  insufficient_funds: "Saldo insuficiente.",
  expired_card: "Cartão expirado.",
  // Sistema
  rate_limited: "Muitas tentativas em pouco tempo. Aguarde um instante.",
  service_unavailable: "Serviço temporariamente fora do ar.",
  network_error: "Sem conexão com o servidor.",
  // WebAuthn / biometria
  no_passkeys: "Nenhuma biometria cadastrada para este email.",
  passkey_failed: "Não foi possível usar a biometria agora."
};

// Padrões de mensagem (regex) → PT-BR, pra DOMException raw e libs externas.
const PATTERN_TO_PT: Array<{ pattern: RegExp; message: string }> = [
  {
    pattern: /timed?\s?out|timeout/i,
    message: "Tempo esgotado. Tente de novo."
  },
  {
    pattern: /not\s?allowed|notallowed/i,
    message: "Operação não permitida no momento."
  },
  {
    pattern: /aborted|cancell?ed/i,
    message: "Operação cancelada."
  },
  {
    pattern: /network\s?error|failed to fetch|networkerror/i,
    message: "Sem conexão com o servidor. Verifique sua internet."
  },
  {
    pattern: /unauthorized|401/i,
    message: "Sua sessão expirou. Faça login de novo."
  },
  {
    pattern: /forbidden|403/i,
    message: "Acesso negado."
  },
  {
    pattern: /not\s?found|404/i,
    message: "Não encontrado."
  },
  {
    pattern: /quota.*exceeded|over.*quota/i,
    message: "Limite de uso atingido."
  },
  // WebAuthn / Credential Manager — erros do Android+iOS
  {
    // "An unknown error occurred while talking to the credential manager"
    // surge no Android quando o dispositivo está sem tela de bloqueio (PIN /
    // impressão digital / face), ou quando os Serviços do Google estão
    // desatualizados. Mensagem com ação concreta.
    pattern: /credential\s?manager/i,
    message:
      "Não foi possível criar a biometria neste dispositivo. Verifique se você tem PIN, impressão digital ou Face ID configurados nas Configurações do celular, e atualize os Serviços do Google se necessário."
  },
  {
    // iOS Safari: às vezes devolve "The operation either timed out or was not allowed"
    pattern: /timed?\s?out\s?or\s?was\s?not\s?allowed/i,
    message:
      "A criação da biometria foi cancelada ou expirou. Tente novamente e confirme com sua digital / Face ID quando aparecer."
  },
  {
    // Chrome desktop quando excludeCredentials estoura
    pattern: /InvalidStateError|already registered/i,
    message:
      "Este dispositivo já tem uma biometria cadastrada na sua conta. Remova a antiga antes de cadastrar de novo."
  },
  {
    // Sites HTTP sem TLS
    pattern: /not\s?in\s?secure\s?context|requires\s?secure\s?context/i,
    message:
      "Biometria só funciona em conexão segura (HTTPS). Acesse pelo site oficial."
  }
];

interface ErrorLike {
  code?: string;
  name?: string;
  message?: string;
  status?: number;
}

/** Recebe qualquer coisa que pode ter vindo de um catch e devolve uma string
 *  amigável e específica em PT-BR. Tenta na ordem:
 *  1. `overrides` passados explicitamente pelo caller
 *  2. `err.code` no mapa de códigos
 *  3. `err.name` (DOMException name, ex: "NotAllowedError")
 *  4. patterns na `err.message`
 *  5. `fallback` ou DEFAULT_FALLBACK
 *
 *  Sempre faz `console.warn` da mensagem técnica original pra debug. */
export function friendlyError(
  err: unknown,
  opts: FriendlyOptions = {}
): string {
  const e = (err ?? {}) as ErrorLike;
  const code = (e.code ?? "").toLowerCase();
  const name = (e.name ?? "");
  const msg = (e.message ?? "").trim();

  // Sempre logar a versão técnica pra dev poder debugar
  try {
    console.warn("[friendly-error]", { code, name, message: msg });
  } catch {
    // ignore
  }

  // 1. Overrides explícitos
  if (opts.overrides && code && opts.overrides[code]) {
    return opts.overrides[code];
  }
  // 2. Códigos conhecidos
  if (code && CODE_TO_PT[code]) {
    return CODE_TO_PT[code];
  }
  // 3. Nomes de DOMException
  if (name === "NotAllowedError") {
    return "Operação não permitida no momento. Tente novamente.";
  }
  if (name === "AbortError") {
    return "Operação cancelada.";
  }
  if (name === "InvalidStateError") {
    return "Estado inválido pra esta operação.";
  }
  if (name === "SecurityError") {
    return "Bloqueado por motivo de segurança. Acesse via HTTPS.";
  }
  // 4. Patterns na mensagem
  if (msg) {
    for (const p of PATTERN_TO_PT) {
      if (p.pattern.test(msg)) return p.message;
    }
  }
  // 5. Status HTTP genérico
  if (typeof e.status === "number") {
    if (e.status === 401) return CODE_TO_PT["unauthenticated"];
    if (e.status === 403) return CODE_TO_PT["forbidden"];
    if (e.status === 404) return CODE_TO_PT["not_found"];
    if (e.status === 429) return CODE_TO_PT["rate_limited"];
    if (e.status >= 500) return CODE_TO_PT["service_unavailable"];
  }
  return opts.fallback ?? DEFAULT_FALLBACK;
}

/** Versão curta — pra usar em catch sem nem importar `friendlyError`. */
export const fe = friendlyError;
